/**
 * @module features/public/NotFoundPage
 * @description Custom 404 Page for RxDeviceGateway.
 * Features high-aesthetic dark/light layout, subtle grid background,
 * responsive navigation, and direct recovery paths.
 */

import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Home, Moon, Sun, ShieldAlert } from "lucide-react";
import { APP_NAME, ORG_NAME } from "@/config/env";
import { Button } from "@/components/ui/button";

export function NotFoundPage() {
  const navigate = useNavigate();

  const [isDark, setIsDark] = React.useState(() => {
    return document.documentElement.classList.contains("dark");
  });

  const toggleTheme = React.useCallback(() => {
    const nextIsDark = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", nextIsDark);
    localStorage.setItem("theme", nextIsDark ? "dark" : "light");
    setIsDark(nextIsDark);
  }, []);

  React.useEffect(() => {
    document.title = `404 — Page Not Found | ${APP_NAME}`;
  }, []);

  return (
    <div className="min-h-screen w-full flex flex-col justify-between bg-background text-foreground antialiased selection:bg-zinc-200 dark:selection:bg-zinc-800 relative px-4 sm:px-8 py-6">
      {/* Top Header */}
      <header className="w-full max-w-5xl mx-auto flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2.5 font-semibold text-sm sm:text-base tracking-tight hover:opacity-90 transition-opacity"
        >
          <div className="h-6 w-6 rounded-full border-[2.5px] border-foreground flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-foreground" />
          </div>
          <span>{APP_NAME}</span>
        </Link>

        {/* Minimal theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/80 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? (
            <Sun className="h-4 w-4 text-amber-400" />
          ) : (
            <Moon className="h-4 w-4 text-zinc-600" />
          )}
        </button>
      </header>

      {/* Center 404 Hero */}
      <main className="w-full max-w-lg mx-auto flex flex-col items-center text-center py-12 sm:py-16 my-auto">
        {/* Glowing Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-medium border border-border/80 bg-muted/60 dark:bg-zinc-900 text-muted-foreground mb-6 shadow-xs">
          <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
          <span>Seems like you have lost !!</span>
        </div>

        {/* Big Aesthetic 404 Heading */}
        <h1 className="text-6xl sm:text-8xl font-black tracking-tight text-foreground font-mono select-none">
          404
        </h1>

        <h2 className="mt-4 text-xl sm:text-2xl font-bold tracking-tight text-foreground font-sans">
          Lost in the Gateway
        </h2>

        <p className="mt-2 text-xs sm:text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
          The requested endpoint or resource does not exist in the platform registry. It may have been decommissioned, relocated, or temporarily disconnected.
        </p>

        {/* Navigation Actions */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-2.5 w-full max-w-xs sm:max-w-none">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto h-10 px-4 text-xs gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Go back
          </Button>

          <Button
            onClick={() => navigate("/login")}
            className="w-full sm:w-auto h-10 px-4 text-xs gap-1.5 bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
          >
            <Home className="h-3.5 w-3.5" />
            Client Portal
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full max-w-5xl mx-auto py-4 text-center text-xs text-muted-foreground border-t border-border/60">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <p>&copy; {new Date().getFullYear()} {ORG_NAME}. All rights reserved.</p>
          <div className="flex items-center gap-3 font-mono text-[11px]">
            <Link to="/terms" className="hover:underline">Terms</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:underline">Privacy</Link>
            <span>·</span>
            <Link to="/login" className="hover:underline">Portal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
